import React from 'react'

import { BuyerIntentLeadClick, normalizeBuyerIntentId, warnMissingBuyerIntentIds } from 'components/BuyerIntent'
import CtaButton from 'components/CtaButton'
import Typography from 'components/Typography'
import { useEventContext } from 'context/EventContext'
import { getPricingTexts, mapPricingCategories } from 'helpers/dataTransformers/pricing'
import { priceFormatter } from 'helpers/formatters'
import useViewport from 'hooks/useViewport'

import classes from './PricingSection.module.scss'

const PricingSection = ({ listing, onShowPricingModal, hasDemoForm }) => {
  const { down } = useViewport()

  const { periodicityText, pricingModelText } = getPricingTexts(listing?.pricing)
  const { hasFreeTrial, isFree } = mapPricingCategories(listing?.pricing?.categories)
  const hasAmount = !!listing?.pricing?.amount
  const hasPrice = Number(listing?.pricing?.amount) > 0
  const startingPriceText =
    hasAmount && hasPrice
      ? `${listing.pricing.currency_symbol}${priceFormatter(listing.pricing?.amount)} ${pricingModelText}${
          periodicityText ? `/${periodicityText}` : ''
        }`
      : ''

  const { addEvent } = useEventContext({ engagementComponent: 'best-product_card-item' })
  const onClickPricing = () => onShowPricingModal({ pGuid: listing.global_product_id })
  const buyerIntentProductId = normalizeBuyerIntentId(listing.global_product_id)

  if (hasDemoForm && !buyerIntentProductId) {
    warnMissingBuyerIntentIds('best-product-detailed:get-price', {
      productId: listing.global_product_id,
    })
  }

  return (
    <>
      <Typography variant="heading-md" className={classes.subtitle}>
        Pricing
      </Typography>
      {down('md') ? (
        <ul className={classes.pricingList}>
          {hasAmount && hasPrice && (
            <li data-testid="list-starting-price">
              <span className={classes.bold}>Starting price:</span>
              {startingPriceText}
            </li>
          )}
          <li data-testid="list-free-version">
            <span className={classes.bold}>Free version:</span>
            {isFree ? 'Available' : 'Not available'}
          </li>
          <li data-testid="list-free-trial">
            <span className={classes.bold}>Free trial:</span>
            {hasFreeTrial ? 'Available' : 'Not available'}
          </li>
        </ul>
      ) : (
        <Typography variant="body-xs" className={classes.pricingRow}>
          {hasAmount && hasPrice && (
            <>
              <span data-testid="row-starting-price">
                <span className={classes.bold}>Starting price:</span>
                {startingPriceText}
              </span>
              <span className={classes.divider}>|</span>
            </>
          )}
          <span data-testid="row-free-version">
            <span className={classes.bold}>Free version:</span>
            {isFree ? 'Available' : 'Not available'}
          </span>
          <span className={classes.divider}>|</span>
          <span data-testid="row-free-trial">
            <span className={classes.bold}>Free trial:</span>
            {hasFreeTrial ? 'Available' : 'Not available'}
          </span>
        </Typography>
      )}
      <Typography variant="body-xs">Prices may vary depending on the number of users and features.</Typography>

      {hasDemoForm &&
        (!buyerIntentProductId ? (
          <CtaButton
            data-testid="listing-item_main_cta"
            className={classes.pricingButton}
            onClick={onClickPricing}
            variant="outlined"
            {...addEvent({
              engagementDetails: 'button_get_price',
              engagementDestination: 'go-to_ppl-price-form',
              name: 'engagement_product_click',
            })}
          >
            get price
          </CtaButton>
        ) : (
          <BuyerIntentLeadClick productId={buyerIntentProductId} sourceLocation="best-product-detailed:get-price">
            <CtaButton
              data-testid="listing-item_main_cta"
              className={classes.pricingButton}
              onClick={onClickPricing}
              variant="outlined"
              {...addEvent({
                engagementDetails: 'button_get_price',
                engagementDestination: 'go-to_ppl-price-form',
                name: 'engagement_product_click',
              })}
            >
              get price
            </CtaButton>
          </BuyerIntentLeadClick>
        ))}
    </>
  )
}

export default PricingSection
